import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Card, DrawerSection, StatusMessage } from '@aimsos/ui';

import { widgetTypeCopyKeys } from '../constants/widgetTypeCopy.js';
import { useLivePreviewWidget } from '../hooks/useLivePreviewWidget.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import { useWidgetPreviewLayout } from '../hooks/useWidgetPreviewLayout.js';
import { widgetTypeLabel } from '../utils/widgetTypeLabel.js';
import LivePreviewCard from './LivePreviewCard.jsx';
import PreviewSizeControl from './PreviewSizeControl.jsx';

import { LayoutGrid } from 'lucide-react';

/**
 * The `LIVE PREVIEW` column (CT-5199).
 *
 * With DS-1 removing previews from the catalog list, this pane and the library
 * detail panel are the only places a widget is seen before use — so the two
 * share `widgetTypeCopy.js` and read the same `About` / `Best for` text for a
 * given type.
 *
 * Once the widget is saved, the preview draws its **real** result:
 * `widget/execute` runs the stored definition, so a uuid exists from the start
 * in edit mode and from the first save in create mode. Before that — and
 * whenever the form has unsaved changes — it falls back to the design-system
 * sample, because the stored result no longer describes what is on screen.
 */
function LivePreviewPane({
  form, columns, widgetUuid, version,
}) {
  const { t } = useTranslation('dashboards');
  const [timeRange, setTimeRange] = useState('last30');

  const { name, type, dataset, previewSize, setPreviewSize } = form;
  const { previewWidget, filterChips, emptyStateKey } = useLivePreviewWidget({ form, columns });

  // Real data, but only while the query matches what is stored.
  //
  // `execute` runs the *saved* definition. An unsaved edit to the data —
  // switching to a record set, adding a grouper — would otherwise leave the
  // previous result on screen under a configuration that no longer produces
  // it, which reads as a preview that ignores your changes. That falls back to
  // the sample, and the save moves `versionStamp`, so the numbers come back
  // matching what was just stored. A change that only redraws the result —
  // the S/M/L size, the name, the type — keeps the real data.
  const { data, hasNoData, isLoading } = useWidgetData({
    widgetUuid,
    version,
    enabled: !form.isDataDirty,
  });

  // The rail and the grid's own resize handles set the same width, so the
  // layout is derived from `previewSize` and reports a resize back to it.
  const { layouts, onLayoutChange } = useWidgetPreviewLayout({
    type: previewWidget.type,
    size: previewSize,
    onSizeChange: setPreviewSize,
  });

  const handleTimeRange = useCallback((next) => setTimeRange(next), []);

  const title = name.trim() || t('widgetBuilder.preview.untitled');

  const metaLine = useMemo(() => {
    const integrationKey = dataset.sourceIntegration === 'aims_os'
      ? 'aimsOs'
      : dataset.sourceIntegration;
    const parts = [
      type ? widgetTypeLabel(type, t) : null,
      dataset.sourceLabel || dataset.sourceId || null,
      integrationKey ? t(`widgetBuilder.data.integrations.${integrationKey}`) : null,
    ].filter(Boolean);
    return parts.join(' · ');
  }, [dataset.sourceId, dataset.sourceIntegration, dataset.sourceLabel, t, type]);

  const copyKeys = useMemo(() => widgetTypeCopyKeys(type), [type]);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
          {t('widgetBuilder.preview.title')}
        </p>
        <PreviewSizeControl value={previewSize} onChange={setPreviewSize} />
      </div>

      {emptyStateKey ? (
        <Card className="p-2">
          <StatusMessage
            icon={LayoutGrid}
            title={t('widgetBuilder.preview.nothingYet')}
            subtitle={t(emptyStateKey)}
          />
        </Card>
      ) : (
        <>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <p className="m-0 min-w-0 truncate text-xs text-content-muted">{metaLine}</p>
            {/* A widget being built has not been governed yet. */}
            <Badge size="small" variant="neutral">
              {t('widgetBuilder.preview.ungoverned')}
            </Badge>
          </div>

          <LivePreviewCard
            title={title}
            previewWidget={previewWidget}
            filterChips={filterChips}
            size={previewSize}
            layouts={layouts}
            onLayoutChange={onLayoutChange}
            timeRange={timeRange}
            onTimeRangeChange={handleTimeRange}
            data={data}
            hasEmptyResult={hasNoData}
            isLoadingData={isLoading}
          />

          <p className="m-0 text-center text-[11px] text-content-muted">
            {t(`widgetBuilder.preview.sizeCaptions.${previewSize}`)}
          </p>

          {copyKeys ? (
            <>
              <DrawerSection title={t('widgetBuilder.preview.about')}>
                <p className="m-0 text-xs text-content-secondary">{t(copyKeys.about)}</p>
              </DrawerSection>
              <DrawerSection title={t('widgetLibrary.detail.bestFor')}>
                <p className="m-0 text-xs text-content-secondary">{t(copyKeys.bestFor)}</p>
              </DrawerSection>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

LivePreviewPane.propTypes = {
  form: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The saved widget's uuid; absent until the first save. */
  widgetUuid: PropTypes.string,
  /** Moves on every write, which is what re-runs the execution. */
  version: PropTypes.string,
};

LivePreviewPane.defaultProps = {
  widgetUuid: undefined,
  version: undefined,
};

export default LivePreviewPane;
